import { useFlag } from '@reflag/react-sdk';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type {
    NavigationChangeRequestAgentSideSheet,
    NavigationScreenSpaceState,
} from '@gitbook/app-navigation';
import { useHiveRevision } from '@gitbook/hive-react';
import {
    Badge,
    Heading,
    SidePanel,
    SidePanelBody,
    SidePanelCloseButton,
    SidePanelExpandButton,
    SidePanelHeader,
    SidePanelHeaderActions,
    SidePanelHeaderMain,
    SidePanelHeaderTitle,
} from '@gitbook/spine-react';

import {
    AgentBlankslateRequestAccess,
    AgentChangeRequestSidePanel,
    useCurrentHivePage,
} from '../../../Components';
import { useHasAIEnabled } from '../../../Components/Agent';
import { useSetAnalyticsScreen, useSideSheetNavigation } from '../../../navigation';

type ChangeRequestAgentSidePanelProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    screen: NavigationScreenSpaceState;
};

/**
 * Sidepanel for GitBook Agent for change request.
 */
export function ChangeRequestAgentSidePanel(
    props: ChangeRequestAgentSidePanelProps
): React.ReactElement | null {
    const { isEnabled } = useFlag('DOCS_AGENTS');
    const hasOrganizationAIEnabled = useHasAIEnabled();
    const { close } = useSideSheetNavigation<NavigationChangeRequestAgentSideSheet>('agent');

    if (!hasOrganizationAIEnabled) {
        return null;
    }

    if (!isEnabled) {
        return (
            <SidePanel expandable onClose={close}>
                <SidePanelHeader>
                    <SidePanelHeaderMain>
                        <SidePanelHeaderTitle>
                            <Heading>GitBook Agent</Heading>
                            <Badge size="xsmall" kind="alpha" label="Early access" />
                        </SidePanelHeaderTitle>
                        <SidePanelHeaderActions>
                            <SidePanelExpandButton />
                            <SidePanelCloseButton />
                        </SidePanelHeaderActions>
                    </SidePanelHeaderMain>
                </SidePanelHeader>
                <SidePanelBody className="p-6">
                    <AgentBlankslateRequestAccess />
                </SidePanelBody>
            </SidePanel>
        );
    }

    return <ChangeRequestAgentSidePanelEnabled {...props} />;
}

/**
 * Sidepanel when the feature is enabled,
 */
function ChangeRequestAgentSidePanelEnabled(
    props: ChangeRequestAgentSidePanelProps
): React.ReactElement | null {
    const { space, changeRequest, analyticsContext, screen } = props;
    const { close } = useSideSheetNavigation<NavigationChangeRequestAgentSideSheet>('agent');

    useSetAnalyticsScreen({ ...analyticsContext, screen: 'space_change_request_agent' }, []);

    // Get revision and page
    const { revision } = useHiveRevision();
    const [page] = useCurrentHivePage(revision, screen.page);

    return (
        <AgentChangeRequestSidePanel
            space={space}
            changeRequest={changeRequest}
            currentContext={{
                location: page
                    ? {
                          spaceId: space.id,
                          pageId: page.id,
                      }
                    : undefined,
            }}
            onClose={close}
        />
    );
}
